import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { trackMetaEvent } from '../utils/utm';
import { trackPurchase as trackMarketingPurchase } from '../utils/marketingEvents';

function amountToDecimal(cents) {
  const amount = Number(cents || 0);
  if (!Number.isFinite(amount)) return 0;
  return Math.round(amount) / 100;
}

function getEventId(searchParams, sessionData) {
  return searchParams.get('session_id') ||
    sessionData?.sessionId ||
    sessionData?.session?.id ||
    sessionData?.id ||
    sessionData?.payment_intent ||
    sessionData?.session?.payment_intent ||
    searchParams.get('conversion_event_id') ||
    sessionData?.metadata?.conversion_event_id ||
    sessionData?.session?.metadata?.conversion_event_id;
}

const CONFIRMED_PAYMENT_STATUSES = new Set(['paid', 'no_payment_required']);

export function isConfirmedPaymentStatus(paymentStatus) {
  return CONFIRMED_PAYMENT_STATUSES.has(paymentStatus);
}

const PurchaseConversionTracker = () => {
  const location = useLocation();

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const sessionId = searchParams.get('session_id');
    if (!sessionId || sessionId === '{CHECKOUT_SESSION_ID}') return;

    const storageKey = `adu_purchase_tracked_${sessionId}`;
    try {
      if (sessionStorage.getItem(storageKey)) return;
    } catch (_e) {
      // Ignore storage failures; tracking should still be best-effort.
    }

    let cancelled = false;

    async function verifyAndTrackPurchase() {
      try {
        const response = await fetch('/api/getCheckoutSession', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ sessionId }),
        });

        if (!response.ok) return;
        const sessionData = await response.json();
        if (
          cancelled ||
          !sessionData?.success ||
          !isConfirmedPaymentStatus(sessionData.payment_status)
        ) return;

        const eventId = getEventId(searchParams, sessionData);
        if (!eventId) return;
        const value = amountToDecimal(sessionData.amount_total);
        const currency = (sessionData.currency || 'usd').toUpperCase();

        trackMetaEvent(
          'Purchase',
          {
            value,
            currency,
            event_id: eventId,
          },
          { eventID: eventId }
        );
        trackMarketingPurchase({
          tier: searchParams.get('tier') ||
            sessionData?.metadata?.tier ||
            sessionData?.session?.metadata?.tier,
          eventId,
          value,
          currency,
        });

        try {
          sessionStorage.setItem(storageKey, eventId);
        } catch (_e) {
          // No-op.
        }
      } catch (error) {
        console.warn('Purchase conversion tracking failed:', error);
      }
    }

    verifyAndTrackPurchase();
    return () => {
      cancelled = true;
    };
  }, [location.search]);

  return null;
};

export default PurchaseConversionTracker;
